import {IconButton, InputAdornment, TextField} from '@mui/material';
import {useField, useFormikContext} from 'formik';
import {FormFieldProps} from '../../model/form';
import React, {useCallback, useEffect, useRef, useState} from "react";
import {Clear} from "@mui/icons-material";
import {dateToGuiAs} from "../../helpers/date";

interface Props extends FormFieldProps {
    type?: string;
    maxlength?: number;
    formatValue?: (value: any) => any;
    normalizeValue?: (value: any) => any;
    rows?: number;
    minRows?: number;
    maxRows?: number;
    minValue?: any;
    maxValue?: any;
    placeholder?: string;
    autoFocus?: boolean;
    clearable?: boolean;
    title?: string;
}

const INPUT_DELAY = 500;

interface PropsPlain extends Props {
    currentValue: any,
    showError?: boolean,
    error?: string,
    onEnter?: (value: any) => void
}


export const TextFormFieldPlain = (props: PropsPlain) => {
    const {
        name, label, type, maxlength,
        onChange, onBlur, clearable, onEnter,
        currentValue, showError, error, title
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);

    const handleOnChange = useCallback((e: any) => {
        if (onChange) {
            onChange(e.target.value);
        }
    }, [onChange]);

    const handleOnBlur = useCallback((e: any) => {
        if (onBlur) {
            onBlur(e.target.value);
        }
    }, [onBlur]);

    const handleKeyDown = useCallback((e: any) => {
        if (e.key === 'Enter') {
            const newValue = (e.target as any)['value'];
            if (onEnter) {
                onEnter(newValue);
            }
        }
    }, [onEnter]);

    const trapEnter = useCallback((e: any) => {
        if (e.key === 'Enter') {
            handleOnBlur(e);
            e.preventDefault();
        }
    }, [handleOnBlur]);

    return <TextField
        className={props.readonly ? 'input-readonly' : (type === 'date' ? 'input-date' : undefined)}
        inputRef={inputRef}
        margin={'dense'}
        fullWidth
        id={name}
        name={name}
        label={label}
        hiddenLabel={!label}
        title={title || (typeof label == 'string' ? label : props.placeholder)}
        autoFocus={props.autoFocus}
        type={type || 'text'}
        value={currentValue === undefined ? '' : currentValue}
        onChange={handleOnChange}
        onKeyDown={type === 'textarea' ? undefined : (type === 'date' ? trapEnter : handleKeyDown)}
        onBlur={handleOnBlur}
        error={showError}
        helperText={showError && error}
        variant={'outlined'}
        size={'small'}
        inputProps={{
            maxLength: maxlength || 100,
            min: props.minValue,
            max: (props.maxValue && (type === 'date' || type === 'datetime-local'))
                ? (type === 'datetime-local' ? dateToGuiAs(props.maxValue, "yyyy-MM-dd'T'HH:mm:ss") : dateToGuiAs(props.maxValue, "yyyy-MM-dd"))
                : props.maxValue,
            readOnly: props.readonly
            // style: !!clearable ? {
            //     marginRight: '-14px'
            // } : undefined
        }}
        InputProps={!!clearable ? {
            endAdornment: !!currentValue ?
                <InputAdornment position="end">
                    <IconButton onClick={() => {
                        if (inputRef.current) {
                            // setValue('');
                            inputRef.current.value = '';
                            handleOnBlur({type: 'blur', target: inputRef.current, bubbles: true});
                        }
                    }}>
                        <Clear/>
                    </IconButton>
                </InputAdornment>
                : undefined
        } : undefined}
        multiline={type === 'textarea'}
        minRows={props.minRows}
        maxRows={props.maxRows}
        rows={props.rows}
        placeholder={props.placeholder}
        disabled={props.disabled}
    >
    </TextField>;
}


export const TextFormField = (props: Props) => {
    const {
        name, onChange, onBlur, formatValue, normalizeValue,
        minValue, maxValue
    } = props;

    const [field, meta, {setValue}] = useField(name);
    const {submitCount} = useFormikContext();
    const [innerValue, setInnerValue] = useState('');
    const timeoutHandle = useRef<any>(null);

    const showError = !!meta.error && ((meta.touched && !!innerValue) || submitCount > 0);

    const formValue = field.value;
    useEffect(() => {
        if (timeoutHandle.current) {
            // ignore
            return;
        }
        if (formValue || formValue === 0) {
            setInnerValue(formatValue ? formatValue(formValue as string) : formValue as string);
        } else {
            setInnerValue('');
        }
    }, [formValue, formatValue]);

    const handleOnChange = useCallback((newValue: any) => {
        if (maxValue && parseInt(newValue) > maxValue) {
            newValue = maxValue;
        } else if (minValue !== undefined && parseInt(newValue) < minValue) {
            newValue = minValue;
        }
        setInnerValue(newValue);
        if (timeoutHandle.current) {
            clearTimeout(timeoutHandle.current);
        }
        timeoutHandle.current = setTimeout(() => {
            const normalized = normalizeValue ? normalizeValue(newValue) : newValue;
            setValue(normalized);
            if (onChange) {
                onChange(normalized);
            }
            timeoutHandle.current = null;

        }, INPUT_DELAY);
    }, [setValue, onChange, normalizeValue, minValue, maxValue]);

    const handleOnBlur = useCallback((newValue: any) => {
        const isChanged = newValue !== innerValue;
        setInnerValue(newValue);

        const normalized = normalizeValue ? normalizeValue(newValue) : newValue;
        if (timeoutHandle.current) {
            clearTimeout(timeoutHandle.current);
            setValue(normalized, true);
            if (onChange) {
                onChange(normalized);
            }
            timeoutHandle.current = null;

        } else if (isChanged) {
            setValue(normalized, true);
        }
        if (onBlur) {
            onBlur(normalized);
        }
        timeoutHandle.current = null;

    }, [setValue, onChange, onBlur, normalizeValue, innerValue]);

    const handleOnEnter = useCallback((newValue: any) => {
        setInnerValue(newValue);
        if (timeoutHandle.current) {
            clearTimeout(timeoutHandle.current);
        }

        const normalized = normalizeValue ? normalizeValue(newValue) : newValue;
        setValue(normalized, true);
        if (onChange) {
            onChange(normalized);
        }
        timeoutHandle.current = null;

    }, [setValue, onChange, normalizeValue]);

    return <TextFormFieldPlain {...props}
        currentValue={innerValue}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        onEnter={handleOnEnter}
        showError={showError}
        error={meta.error}
    />;
}
